<template>
  <div class="card mt-4">
    <div class="card-header">
      <h5 class="mb-0">Import Word Scope Pricing</h5>
    </div>
    <div class="card-body">
      <div class="alert alert-warning" role="alert">
        Overridden prices cannot be retrieved or reverted.
      </div>

      <div
        class="scope-import-dropzone"
        :class="{ active: dragActive }"
        @dragover.prevent="dragActive = true"
        @dragleave.prevent="dragActive = false"
        @drop.prevent="handleDrop"
      >
        <div class="dropzone-content text-center">
          <p class="mb-2">Drag and drop a completed Word document here</p>
          <p class="text-muted">or click to select a .docx file</p>
          <button
            class="btn btn-outline-primary btn-sm"
            @click.prevent="triggerFileInput"
          >
            Choose file
          </button>
        </div>
        <input
          ref="fileInput"
          type="file"
          accept=".docx"
          class="d-none"
          @change="handleFileChange"
        />
      </div>

      <div v-if="selectedFile" class="mt-3">
        <div><strong>Selected file:</strong> {{ selectedFile.name }}</div>
        <div class="text-muted">{{ humanFileSize(selectedFile.size) }}</div>
      </div>

      <div class="mt-3">
        <label class="form-control-label">Select trade to import</label>
        <Multiselect
          :modelValue="selectedTrade"
          :options="tradeOptions"
          mode="single"
          :closeOnSelect="true"
          :searchable="true"
          :createOption="false"
          placeholder="Select one trade"
          @update:modelValue="updateTrade"
        />
      </div>

      <div class="d-flex gap-2 mt-3">
        <button
          class="btn btn-primary"
          :disabled="!selectedFile || !selectedTrade || loading"
          @click="submitImport"
        >
          {{ loading ? "Importing..." : "Import prices" }}
        </button>
        <button
          class="btn btn-outline-secondary"
          :disabled="!selectedFile || loading"
          @click="clearSelection"
        >
          Clear selection
        </button>
      </div>

      <div v-if="error" class="alert alert-danger mt-3" role="alert">
        {{ error }}
      </div>
      <div v-if="successMessage" class="alert alert-success mt-3" role="alert">
        {{ successMessage }}
      </div>

      <div v-if="importedSummary.length" class="mt-4">
        <h6>Import summary</h6>
        <div v-for="row in importedSummary" :key="row.address" class="mb-3">
          <div class="fw-bold">{{ row.address }}</div>
          <div class="small text-muted mb-1">
            Imported {{ row.imported_item_count }} prices
          </div>
          <div>
            <span
              v-for="(count, trade) in row.trades"
              :key="trade"
              class="badge bg-secondary me-1"
            >
              {{ trade }}: {{ count }}
            </span>
          </div>
        </div>
      </div>

      <div v-if="skippedAddresses.length" class="mt-3">
        <h6>Skipped addresses</h6>
        <ul class="mb-0">
          <li v-for="address in skippedAddresses" :key="address">
            {{ address }}
          </li>
        </ul>
      </div>

      <div v-if="warnings.length" class="alert alert-warning mt-3" role="alert">
        <div v-for="warning in warnings" :key="warning">{{ warning }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import { useToast } from "vue-toast-notification";
import Multiselect from "@vueform/multiselect";

const props = defineProps({
  contractId: {
    type: String,
    required: true,
  },
  loadContract: {
    type: Function,
    required: true,
  },
});

const toast = useToast();
const store = useStore();
const apiEndpoint = store.state.apiEndpoint;

const selectedFile = ref(null);
const selectedTrade = ref("");
const tradeOptions = ref([]);
const dragActive = ref(false);
const loading = ref(false);
const error = ref("");
const successMessage = ref("");
const importedSummary = ref([]);
const skippedAddresses = ref([]);
const warnings = ref([]);
const fileInput = ref(null);

const token = sessionStorage.getItem("token");
const headers = {
  Authorization: `Bearer ${token}`,
};

const isDocxFile = (file) => Boolean(file?.name?.toLowerCase().endsWith(".docx"));

const fetchTradeOptions = async () => {
  try {
    const response = await axios.get(`${apiEndpoint}/trades/`, { headers });
    tradeOptions.value = (response.data || [])
      .map((trade) => (trade.code || "").trim().toUpperCase())
      .filter(Boolean);
  } catch (err) {
    toast.error("Failed to load trades.");
    console.error(err);
  }
};

const triggerFileInput = () => {
  if (fileInput.value) {
    fileInput.value.click();
  }
};

const handleFileChange = (event) => {
  clearMessages();
  const file = event.target.files?.[0];
  if (file) {
    if (!isDocxFile(file)) {
      error.value = "Please select a .docx file.";
      selectedFile.value = null;
      return;
    }
    selectedFile.value = file;
  }
};

const handleDrop = (event) => {
  dragActive.value = false;
  clearMessages();
  const file = event.dataTransfer?.files?.[0];
  if (!file) {
    return;
  }
  if (!isDocxFile(file)) {
    error.value = "Please drop a .docx file.";
    selectedFile.value = null;
    return;
  }
  selectedFile.value = file;
};

const updateTrade = (value) => {
  selectedTrade.value = value ? String(value).trim().toUpperCase() : "";
};

const clearMessages = () => {
  error.value = "";
  successMessage.value = "";
  importedSummary.value = [];
  skippedAddresses.value = [];
  warnings.value = [];
};

const clearSelection = () => {
  clearMessages();
  selectedFile.value = null;
  selectedTrade.value = "";
};

const humanFileSize = (size) => {
  if (size === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.floor(Math.log(size) / Math.log(1024));
  return `${(size / 1024 ** index).toFixed(1)} ${units[index]}`;
};

const submitImport = async () => {
  clearMessages();
  if (!selectedFile.value) {
    error.value = "Please select a Word document to upload.";
    return;
  }
  if (!selectedTrade.value) {
    error.value = "Please select one trade.";
    return;
  }

  loading.value = true;
  const formData = new FormData();
  formData.append("scopeDocx", selectedFile.value);
  formData.append("trade_codes", selectedTrade.value);

  try {
    const response = await axios.post(
      `${apiEndpoint}/contracts/${props.contractId}/import_scope_docx/`,
      formData,
      {
        headers: {
          ...headers,
          "Content-Type": "multipart/form-data",
        },
      },
    );

    const data = response.data || {};
    if (data.success) {
      successMessage.value = "Import completed successfully.";
      importedSummary.value = data.imported || [];
      skippedAddresses.value = data.skipped_addresses || [];
      warnings.value = data.warnings || [];
      if (props.loadContract) {
        await props.loadContract();
      }
    } else {
      error.value = data.message || "Import failed.";
    }
  } catch (err) {
    console.error(err);
    error.value =
      err.response?.data?.detail || "Failed to import the Word document.";
  } finally {
    loading.value = false;
  }
};

onMounted(fetchTradeOptions);
</script>

<style scoped>
@import "@vueform/multiselect/themes/default.css";

.scope-import-dropzone {
  border: 2px dashed #ced4da;
  border-radius: 0.5rem;
  padding: 24px;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;
  cursor: pointer;
}

.scope-import-dropzone.active {
  border-color: #2563eb;
  background-color: #f0f7ff;
}

.dropzone-content {
  pointer-events: none;
}

.scope-import-dropzone .btn {
  pointer-events: auto;
}

:deep(.multiselect) {
  min-height: 44px;
  font-size: 14px;
}

:deep(.multiselect-tag) {
  background: #0d6efd;
  color: white;
}

:deep(.multiselect-dropdown) {
  z-index: 1100;
}
</style>
